import { supabase } from "@/lib/supabase";
import {
  TABLE_SORTABLE_COLUMNS,
  type ActivityTableRow,
  type TableQueryOpts,
} from "@/types/table.types";
import { nextInstantIso } from "@/utils/date.utils";

export async function fetchTableActivities(
  q: TableQueryOpts,
): Promise<{ rows: ActivityTableRow[]; total: number }> {
  const pageSize = Math.max(1, Math.min(q.pageSize, 200));

  let req = supabase
    .from("activities")
    .select(
      "strava_activity_id,start_date,name,type,sport_type,distance,moving_time,total_elevation_gain",
      { count: "exact" },
    )
    .eq("user_id", q.userId)
    .eq("is_deleted", false);

  if (q.search?.trim()) {
    const term = q.search.trim().replace(/[%_]/g, "\\$&");
    req = req.or(
      `name.ilike.%${term}%,sport_type.ilike.%${term}%,type.ilike.%${term}%`,
    );
  }

  switch (q.sports?.mode) {
    case "include": {
      if (q.sports.sports.length === 0) {
        req = req.eq("sport_type", "__none__");
      } else {
        req = req.in("sport_type", q.sports.sports);
      }
      break;
    }
    case "exclude": {
      if (q.sports.sports.length > 0) {
        req = req.not("sport_type", "in", `(${q.sports.sports.join(",")})`);
      }
      break;
    }
    case "all":
    default:
      break;
  }

  if (q.from) req = req.gte("start_date", q.from);
  if (q.to) req = req.lt("start_date", nextInstantIso(q.to));

  const sort =
    q.sortBy?.id && TABLE_SORTABLE_COLUMNS.includes(q.sortBy.id)
      ? q.sortBy.id
      : "start_date";
  const desc = q.sortBy?.desc ?? true;

  req = req.order(sort, { ascending: !desc });

  const rangeFrom = q.pageIndex * pageSize;
  const rangeTo = rangeFrom + pageSize - 1;

  const { data, error, count } = await req.range(rangeFrom, rangeTo);

  if (error) throw error;

  return {
    rows: (data ?? []) as ActivityTableRow[],
    total: count ?? 0,
  };
}
